import isNil from 'lodash/isNil';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setMeasureAxisMaxValue,
  setMeasureAxisMinValue,
  setMeasureAxisScale,
  setSecondaryMeasureAxisMaxValue,
  setSecondaryMeasureAxisMinValue,
  setSecondaryMeasureAxisScale,
} from 'common/authoring_workflow/actions';
import {
  getMeasureAxisMaxValue,
  getMeasureAxisMinValue,
  getMeasureAxisScale,
  getSecondaryMeasureAxisMaxValue,
  getSecondaryMeasureAxisMinValue,
  getSecondaryMeasureAxisScale,
  hasComboChartLineSeries,
  isUsingPrimaryAxis,
  isUsingPrimaryAxisOnly,
  isUsingSecondaryAxis,
  isUsingSecondaryAxisForLines,
  isUsingSecondaryAxisOnly,
} from 'common/authoring_workflow/selectors/vifAuthoring';
import { AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';
import { AxisScaleSelector } from './AxisScaleSelector';
import Tabs from '../../shared/Tabs';
import { I18n } from 'common/i18n';
import {
  MEASURE_AXIS_SCALE_CUSTOM,
  MEASURE_AXIS_SCALE_MIN_TO_MAX,
  MEASURE_AXIS_SCALE_ZERO_TO_MAX,
} from 'common/authoring_workflow/constants';

const scope = 'shared.visualizations.panes.data.fields.combo_chart_measure_axis_options';

const primaryAxisTabIndex = 0;
const secondaryAxisTabIndex = 1;

const getPrimaryAxisScale = (vifAuthoring: object): string => {
  const maxValue = getMeasureAxisMaxValue(vifAuthoring);
  const minValue = getMeasureAxisMinValue(vifAuthoring);
  if (!isNil(maxValue) || !isNil(minValue)) {
    return MEASURE_AXIS_SCALE_CUSTOM;
  } else if (getMeasureAxisScale(vifAuthoring) === MEASURE_AXIS_SCALE_MIN_TO_MAX) {
    return MEASURE_AXIS_SCALE_MIN_TO_MAX;
  } else {
    return MEASURE_AXIS_SCALE_ZERO_TO_MAX;
  }
};

const getSecondaryAxisScale = (vifAuthoring: object): string => {
  const maxValue = getSecondaryMeasureAxisMaxValue(vifAuthoring);
  const minValue = getSecondaryMeasureAxisMinValue(vifAuthoring);
  if (!isNil(maxValue) || !isNil(minValue)) {
    return MEASURE_AXIS_SCALE_CUSTOM;
  } else if (getSecondaryMeasureAxisScale(vifAuthoring) === MEASURE_AXIS_SCALE_MIN_TO_MAX) {
    return MEASURE_AXIS_SCALE_MIN_TO_MAX;
  } else {
    return MEASURE_AXIS_SCALE_ZERO_TO_MAX;
  }
};

// add selectedTabIndex = 0 as a prop?
export const AxisScaleSelectorDual = ({ selectedTabIndex = 0 }: { selectedTabIndex?: number }) => {
  const vifAuthoring = useSelector((state: AuthoringWorkflowState) => state.vifAuthoring);
  const [tabIndex, setTabIndex] = useState(selectedTabIndex);
  const [primaryAxisScale, setPrimaryAxisScale] = useState(getPrimaryAxisScale(vifAuthoring));
  const [secondaryAxisScale, setSecondaryAxisScale] = useState(getSecondaryAxisScale(vifAuthoring));
  const dispatch = useDispatch();

  useEffect(() => {
    if (
      !isUsingPrimaryAxis(vifAuthoring) ||
      (isUsingSecondaryAxisForLines(vifAuthoring) && primaryAxisScale === MEASURE_AXIS_SCALE_MIN_TO_MAX)
    ) {
      setPrimaryAxisScale(MEASURE_AXIS_SCALE_ZERO_TO_MAX);
    }
    if (
      !isUsingSecondaryAxis(vifAuthoring) ||
      (!isUsingSecondaryAxisForLines(vifAuthoring) && secondaryAxisScale === MEASURE_AXIS_SCALE_MIN_TO_MAX)
    ) {
      setSecondaryAxisScale(MEASURE_AXIS_SCALE_ZERO_TO_MAX);
    }
  }, [vifAuthoring, primaryAxisScale, secondaryAxisScale]);

  const isPrimaryAxis = useCallback(() => tabIndex === primaryAxisTabIndex, [tabIndex]);

  const onMaxValueTextboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isPrimaryAxis()) {
      dispatch(setMeasureAxisMaxValue(event.target.value));
      dispatch(setMeasureAxisScale());
    } else {
      dispatch(setSecondaryMeasureAxisMaxValue(event.target.value));
      dispatch(setSecondaryMeasureAxisScale());
    }
  };

  const onMinValueTextboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isPrimaryAxis()) {
      dispatch(setMeasureAxisMinValue(event.target.value));
      dispatch(setMeasureAxisScale());
    } else {
      dispatch(setSecondaryMeasureAxisMinValue(event.target.value));
      dispatch(setSecondaryMeasureAxisScale());
    }
  };

  const onRadioButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const axisScale = event.target.value;
    if (isPrimaryAxis()) {
      setPrimaryAxisScale(axisScale);
      switch (axisScale) {
        case MEASURE_AXIS_SCALE_ZERO_TO_MAX:
          dispatch(setMeasureAxisScale());
          dispatch(setMeasureAxisMaxValue());
          dispatch(setMeasureAxisMinValue());
          break;
        case MEASURE_AXIS_SCALE_MIN_TO_MAX:
          dispatch(setMeasureAxisScale(MEASURE_AXIS_SCALE_MIN_TO_MAX));
          dispatch(setMeasureAxisMaxValue());
          dispatch(setMeasureAxisMinValue());
          break;
      }
    } else {
      setSecondaryAxisScale(axisScale);
      switch (axisScale) {
        case MEASURE_AXIS_SCALE_ZERO_TO_MAX:
          dispatch(setSecondaryMeasureAxisScale());
          dispatch(setSecondaryMeasureAxisMaxValue());
          dispatch(setSecondaryMeasureAxisMinValue());
          break;
        case MEASURE_AXIS_SCALE_MIN_TO_MAX:
          dispatch(setSecondaryMeasureAxisScale(MEASURE_AXIS_SCALE_MIN_TO_MAX));
          dispatch(setSecondaryMeasureAxisMaxValue());
          dispatch(setSecondaryMeasureAxisMinValue());
          break;
      }
    }
  };

  const renderMeasureAxisOptions = () => {
    const axisScale = isPrimaryAxis() ? primaryAxisScale : secondaryAxisScale;
    const maxLimit = isPrimaryAxis()
      ? getMeasureAxisMaxValue(vifAuthoring)
      : getSecondaryMeasureAxisMaxValue(vifAuthoring);
    const minLimit = isPrimaryAxis()
      ? getMeasureAxisMinValue(vifAuthoring)
      : getSecondaryMeasureAxisMinValue(vifAuthoring);
    const disabled =
      (isPrimaryAxis() && isUsingSecondaryAxisOnly(vifAuthoring)) ||
      (!isPrimaryAxis() && isUsingPrimaryAxisOnly(vifAuthoring));
    const disableScaleMinToMaxValue =
      !hasComboChartLineSeries(vifAuthoring) ||
      (isPrimaryAxis() && isUsingSecondaryAxisForLines(vifAuthoring)) ||
      (!isPrimaryAxis() && !isUsingSecondaryAxisForLines(vifAuthoring));
    return (
      <AxisScaleSelector
        axisScale={axisScale}
        disabled={disabled}
        disableScaleMinToMaxValue={disableScaleMinToMaxValue}
        maxLimit={maxLimit || ''}
        minLimit={minLimit || ''}
        onMaxValueTextboxChange={onMaxValueTextboxChange}
        onMinValueTextboxChange={onMinValueTextboxChange}
        onRadioButtonChange={onRadioButtonChange}
      />
    );
  };

  const renderTabs = () => {
    const tabs = [
      {
        onClickTab: () => setTabIndex(primaryAxisTabIndex),
        selected: tabIndex === primaryAxisTabIndex,
        tabIndex: primaryAxisTabIndex,
        title: I18n.translate('primary_axis', { scope }),
      },
      {
        onClickTab: () => setTabIndex(secondaryAxisTabIndex),
        selected: tabIndex === secondaryAxisTabIndex,
        tabIndex: secondaryAxisTabIndex,
        title: I18n.translate('secondary_axis', { scope }),
      },
    ];
    return <Tabs tabs={tabs} />;
  };

  return (
    <div className="measure-axis-scale-dual-container">
      {renderTabs()}
      {renderMeasureAxisOptions()}
    </div>
  );
};
